import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { logoRow, spark } from "@/lib/svg";
import { usd, usd0, dShort, dLong, mName, today, iso, csvOf, download, errText } from "@/lib/format";
import { AlertTriangle, PhoneCall } from "lucide-react";

const BASE_TABS = [
  ["overview", "Overview"],
  ["accounts", "Accounts"],
  ["transactions", "Transactions"],
  ["transfer", "Transfer"],
  ["statements", "Statements"],
  ["profile", "Profile"],
];

export default function Dashboard({ user, onSignOut }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("overview");
  const [stMonth, setStMonth] = useState(null);
  const [modal, setModal] = useState(null);
  const [reveal, setReveal] = useState({});
  const [focus, setFocus] = useState(null);
  const logoHtml = useRef(logoRow(1)).current;

  const load = async () => {
    try {
      const r = await api.get("/bootstrap");
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 401) {
        onSignOut();
      } else {
        toast.error(errText(e));
      }
    }
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const IDLE_MS = 4 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        toast("Signed out after 4 minutes of inactivity.");
        onSignOut();
      }, IDLE_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, []);

  const closeModal = () => setModal(null);
  const nav = (v, code) => {
    if (code !== undefined) setFocus(code);
    setStMonth(null);
    setView(v);
    closeModal();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!data) return <div className="main"><div className="sub">Loading…</div></div>;

  const tabs = data.is_admin ? [...BASE_TABS, ["admin", "Admin"]] : BASE_TABS;
  const acct = (code) => data.accounts.find((a) => a.code === code);
  const acctLabel = (code) => {
    const a = acct(code);
    return a ? a.name + " ••" + a.num : "External";
  };

  const shared = { data, acct, acctLabel, nav, setModal, closeModal, reveal, setReveal, reload: load, user, focus };

  return (
    <>
      <div className="hdr">
        <div className="logo-row" dangerouslySetInnerHTML={{ __html: logoHtml }} />
        <div className="hdr-r">
          <div className="who">
            <b data-testid="header-user-name">{data.profile.name}</b>
            <span>{data.profile.business ? "Business banking · " + (data.accounts.length) + " accounts" : "Premier Checking ••" + (acct("chk")?.num || "")}</span>
          </div>
          <button className="btn btn-ghost btn-sm" data-testid="signout-button" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>
      <div className="tabs">
        {tabs.map((t) => (
          <button
            key={t[0]}
            className={"tab" + (t[0] === view && !stMonth ? " on" : "")}
            data-testid={`nav-${t[0]}-tab`}
            onClick={() => nav(t[0], null)}
          >
            {t[1]}
          </button>
        ))}
      </div>
      {true && (
        <div className="review-banner" data-testid="account-review-banner">
          <AlertTriangle size={18} className="review-banner-ic" />
          <div className="review-banner-text">
            <b>Transactions are temporarily unavailable while we complete a review of your account.</b>
            <span>Online account access remains available.</span>
          </div>
          <div className="review-banner-actions">
            <button
              className="btn btn-ghost btn-sm"
              data-testid="review-learn-more"
              onClick={() =>
                setModal(
                  <>
                    <h3>Account review in progress</h3>
                    <p className="sub" style={{ lineHeight: 1.6, margin: "0 0 14px" }}>
                      As part of a routine review, outgoing and incoming transactions on this account are
                      paused. You can still view balances, statements and transaction history. No action is
                      required from you while the review is completed.
                    </p>
                    <p className="sub" style={{ lineHeight: 1.6, margin: 0 }}>
                      If you have questions, please contact your relationship team using the details on your
                      welcome letter.
                    </p>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                      <button className="btn btn-navy btn-sm" onClick={closeModal}>Close</button>
                    </div>
                  </>
                )
              }
            >
              Learn more
            </button>
            <button
              className="btn btn-lime btn-sm"
              data-testid="review-contact-support"
              onClick={() =>
                setModal(
                  <>
                    <h3>Contact support</h3>
                    <div className="srow"><span className="l">Client care</span><span className="v mono">1-800-555-0199</span></div>
                    <div className="srow"><span className="l">Hours</span><span className="v">Mon–Fri, 7am–7pm MT</span></div>
                    <div className="srow"><span className="l">Secure message</span><span className="v">support@summitridgebank.example</span></div>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                      <button className="btn btn-navy btn-sm" onClick={closeModal}>Close</button>
                    </div>
                  </>
                )
              }
            >
              <PhoneCall size={14} style={{ marginBottom: -2, marginRight: 5 }} />Contact support
            </button>
          </div>
        </div>
      )}
      <div className="main">
        {stMonth ? (
          <StatementDetail stmt={stMonth} {...shared} />
        ) : view === "overview" ? (
          <Overview {...shared} />
        ) : view === "accounts" ? (
          <Accounts {...shared} />
        ) : view === "transactions" ? (
          <Transactions {...shared} />
        ) : view === "transfer" ? (
          <Transfer {...shared} />
        ) : view === "statements" ? (
          <Statements {...shared} openStmt={(s) => { setFocus(s.code); setStMonth(s); }} />
        ) : view === "profile" ? (
          <Profile {...shared} onSignOut={onSignOut} />
        ) : view === "admin" ? (
          <Admin {...shared} />
        ) : (
          <Overview {...shared} />
        )}
      </div>

      {modal && (
        <div className="modal" onClick={(e) => e.target.classList.contains("modal") && closeModal()}>
          <div className="mcard">{modal}</div>
        </div>
      )}
    </>
  );
}

/* ---------- helpers ---------- */
function postedSorted(data, code) {
  return data.transactions
    .filter((t) => t.acct === code && t.status === "posted")
    .sort((a, b) => (a.date === b.date ? a.seq - b.seq : a.date.localeCompare(b.date)));
}
function seriesFor(data, code, days) {
  const list = postedSorted(data, code);
  const out = [];
  let i = 0,
    bal = data.accounts.find((a) => a.code === code).opening;
  const st = new Date();
  st.setDate(st.getDate() - days);
  for (let d = 0; d <= days; d++) {
    const day = iso(new Date(st.getTime() + d * 86400000));
    while (i < list.length && list[i].date <= day) {
      bal = list[i].balanceAfter;
      i++;
    }
    out.push(bal);
  }
  return out;
}
function txSummary(data, days) {
  const from = iso(new Date(Date.now() - days * 86400000));
  const l = data.transactions.filter((t) => t.status === "posted" && t.method !== "Internal" && t.date >= from);
  return {
    in: l.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0),
    out: l.filter((t) => t.amount < 0).reduce((s, t) => s + t.amount, 0),
  };
}
function combinedSeries(data, days) {
  const per = data.accounts.map((a) => seriesFor(data, a.code, days));
  const out = [];
  for (let i = 0; i <= days; i++) out.push(per.reduce((s, arr) => s + (arr[i] || 0), 0));
  return out;
}

function TxnRow({ t, showBal, onClick, acctName }) {
  const sched = t.status === "scheduled";
  return (
    <tr data-act="txn" data-testid={`txn-row-${t.id}`} onClick={onClick}>
      <td style={{ width: 112, whiteSpace: "nowrap" }} className="mono">
        {dShort(t.date)}
      </td>
      <td>
        <span className="dsc">
          {t.desc}
          {sched && <span className="badge sched">Scheduled</span>}
          <span className="m">
            {t.cat} &nbsp;·&nbsp; {t.method}
            {acctName ? <> &nbsp;·&nbsp; {acctName}</> : null}
          </span>
        </span>
      </td>
      <td className={"r " + (t.amount > 0 ? "pos" : "")}>
        {(t.amount > 0 ? "+" : "") + usd(t.amount)}
      </td>
      {showBal && (
        <td className="r mono" style={{ color: "#9d9d9d" }}>
          {sched ? "—" : usd(t.balanceAfter)}
        </td>
      )}
    </tr>
  );
}

function txnModal(t, acctLabel, closeModal) {
  return (
    <>
      <h3>{t.desc}</h3>
      <div className="srow"><span className="l">Date</span><span className="v">{dLong(t.date)}</span></div>
      <div className="srow"><span className="l">Amount</span><span className={"v " + (t.amount > 0 ? "pos" : "")}>{(t.amount > 0 ? "+" : "") + usd(t.amount)}</span></div>
      <div className="srow"><span className="l">Account</span><span className="v">{acctLabel(t.acct)}</span></div>
      <div className="srow"><span className="l">Category</span><span className="v">{t.cat}</span></div>
      <div className="srow"><span className="l">Method</span><span className="v">{t.method}</span></div>
      {t.cp && <div className="srow"><span className="l">Counterparty</span><span className="v">{t.cp}</span></div>}
      <div className="srow"><span className="l">Reference</span><span className="v mono">{t.ref}</span></div>
      <div className="srow"><span className="l">Status</span><span className="v">{t.status === "scheduled" ? "Scheduled" : "Posted"}</span></div>
      <div className="srow"><span className="l">Balance after</span><span className="v">{t.balanceAfter != null ? usd(t.balanceAfter) : "—"}</span></div>
      {t.memo && <div className="srow"><span className="l">Memo</span><span className="v">{t.memo}</span></div>}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
        <button className="btn btn-ghost btn-sm" data-testid="modal-close-button" onClick={closeModal}>Close</button>
      </div>
    </>
  );
}

/* ---------- Overview ---------- */
function Overview({ data, acct, nav, setModal, closeModal, acctLabel }) {
  const sm = txSummary(data, 30);
  const total = data.accounts.reduce((s, x) => s + x.balance, 0);
  const recent = [...data.transactions]
    .sort((x, y) => (x.date === y.date ? y.seq - x.seq : y.date.localeCompare(x.date)))
    .slice(0, 7);
  const hr = new Date().getHours();
  const greet = hr < 12 ? "Good morning" : hr < 18 ? "Good afternoon" : "Good evening";
  const who = data.profile.business ? data.profile.name : data.profile.name.split(" ")[0];
  const routing = data.accounts[0]?.routing;
  return (
    <div data-testid="overview-view">
      <h1 className="h1">{greet}, {who}</h1>
      <div className="sub" style={{ marginBottom: 18 }}>
        {data.profile.name} &nbsp;·&nbsp; {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
      </div>
      <div className="bal-card" data-testid="overview-balance-card">
        <div className="bal-lbl">{data.profile.business ? "Total available · Business banking" : "Total available"}</div>
        <div className="bal" data-testid="overview-balance-amount">{usd(total)}</div>
        <div className="bal-sub">{data.profile.business ? data.accounts.length + " checking accounts" : data.accounts.map((a) => a.name).join(" · ")} &nbsp;·&nbsp; Routing {routing}</div>
        <div style={{ display: "flex", gap: 9, marginTop: 18, flexWrap: "wrap" }}>
          <button className="btn btn-lime btn-sm" data-testid="overview-transfer-button" onClick={() => nav("transfer")}>Transfer money</button>
          <button className="btn btn-ghost btn-sm" onClick={() => nav("transactions")}>Transactions</button>
          <button className="btn btn-ghost btn-sm" onClick={() => nav("statements")}>Statements</button>
        </div>
      </div>
      <div className="grid g3" style={{ marginTop: 14 }}>
        <div className="card kpi"><div className="k">Money in · 30d</div><div className="v g">{usd0(sm.in)}</div></div>
        <div className="card kpi"><div className="k">Money out · 30d</div><div className="v">{usd0(sm.out)}</div></div>
        <div className="card kpi"><div className="k">Total relationship</div><div className="v">{usd0(total)}</div></div>
      </div>
      <div className="grid" style={{ marginTop: 14, gridTemplateColumns: "repeat(2,1fr)" }}>
        {data.accounts.map((a) => (
          <div className="card" key={a.code} data-testid={`overview-account-${a.code}`}>
            <div className="dsc">{a.name} <span className="badge">••{a.num}</span></div>
            <div className="bal" style={{ fontSize: 22, marginTop: 8 }}>{usd(a.balance)}</div>
            <div className="sub" style={{ marginTop: 4 }}>{a.apy}</div>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} data-testid={`overview-activity-${a.code}`} onClick={() => nav("transactions", a.code)}>View activity</button>
          </div>
        ))}
      </div>
      <div className="card" style={{ marginTop: 14 }}>
        <div className="stat-row" style={{ marginBottom: 10 }}>
          <div className="h1" style={{ fontSize: 15 }}>Balance trend · 90 days</div>
          <div className="sub">All accounts combined</div>
        </div>
        <div dangerouslySetInnerHTML={{ __html: spark(combinedSeries(data, 90), 660, 88) }} />
      </div>
      <div className="card" style={{ marginTop: 14, padding: "6px 14px 10px" }}>
        <div className="stat-row" style={{ padding: "12px 0 4px" }}>
          <div className="h1" style={{ fontSize: 15 }}>Recent activity</div>
          <button className="link" onClick={() => nav("transactions")}>View all</button>
        </div>
        <table className="tbl">
          <tbody>
            {recent.length === 0 && (
              <tr><td colSpan="4" data-testid="overview-empty-activity" style={{ padding: 22, textAlign: "center", color: "#9d9d9d" }}>No activity yet. Transfers you make will appear here.</td></tr>
            )}
            {recent.map((t) => (
              <TxnRow key={t.id} t={t} showBal acctName={acct(t.acct)?.name} onClick={() => setModal(txnModal(t, acctLabel, closeModal))} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Accounts ---------- */
function Accounts({ data, nav, reveal, setReveal }) {
  return (
    <div data-testid="accounts-view">
      <h1 className="h1">Accounts</h1>
      <div className="sub" style={{ marginBottom: 18 }}>
        {data.accounts.length} deposit accounts on this profile. Transfers between them post instantly.
      </div>
      {data.accounts.map((a) => (
        <div className="card" style={{ marginBottom: 13 }} key={a.code} data-testid={`account-card-${a.code}`}>
          <div className="stat-row">
            <div>
              <div className="dsc">{a.name} <span className="badge">{a.type}</span></div>
              <div className="sub mono">•••• {a.num} &nbsp;·&nbsp; Routing {a.routing}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="bal" style={{ fontSize: 25 }}>{usd(a.balance)}</div>
              <div className="sub">{a.apy}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            <button className="btn btn-ghost btn-sm" onClick={() => nav("transfer")}>Transfer</button>
            <button className="btn btn-ghost btn-sm" data-testid={`account-transactions-${a.code}`} onClick={() => nav("transactions", a.code)}>Transactions</button>
            <button className="btn btn-ghost btn-sm" data-testid={`account-statements-${a.code}`} onClick={() => nav("statements", a.code)}>Statements</button>
            <button
              className="btn btn-ghost btn-sm"
              data-testid={`account-reveal-${a.code}`}
              onClick={() => setReveal((r) => ({ ...r, [a.code]: !r[a.code] }))}
            >
              {reveal[a.code] ? "Hide" : "Reveal"} number
            </button>
          </div>
          {reveal[a.code] && (
            <div className="sub mono" style={{ marginTop: 11 }}>Full account number &nbsp;{a.num_full}</div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------- Transactions ---------- */
function Transactions({ data, acct, acctLabel, setModal, closeModal, focus }) {
  const [f, setF] = useState({ q: "", type: "all", cat: "all", acct: focus || "all", from: "", to: "", page: 1, per: 12 });
  const up = (patch) => setF((s) => ({ ...s, ...patch, page: patch.page != null ? patch.page : 1 }));
  const cats = useMemo(() => Array.from(new Set(data.transactions.map((t) => t.cat))).sort(), [data]);

  const list = useMemo(() => {
    const q = f.q.toLowerCase();
    return data.transactions
      .filter((t) => {
        if (f.acct !== "all" && t.acct !== f.acct) return false;
        if (f.type === "credit" && t.amount <= 0) return false;
        if (f.type === "debit" && t.amount >= 0) return false;
        if (f.cat !== "all" && t.cat !== f.cat) return false;
        if (f.from && t.date < f.from) return false;
        if (f.to && t.date > f.to) return false;
        if (q && !(t.desc + " " + t.cat + " " + t.ref + " " + t.cp).toLowerCase().includes(q)) return false;
        return true;
      })
      .sort((a, b) => (a.date === b.date ? b.seq - a.seq : b.date.localeCompare(a.date)));
  }, [data, f]);

  const pages = Math.max(1, Math.ceil(list.length / f.per));
  const page = Math.min(f.page, pages);
  const rows = list.slice((page - 1) * f.per, page * f.per);
  const cin = list.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const cout = list.filter((t) => t.amount < 0).reduce((s, t) => s + t.amount, 0);

  return (
    <div data-testid="transactions-view">
      <h1 className="h1">Transactions</h1>
      <div className="sub" style={{ marginBottom: 15 }}>Posted and scheduled activity across your accounts.</div>
      <div className="filters">
        <div className="field"><span className="lbl">Search</span>
          <input data-testid="transactions-search-input" placeholder="Description, category, reference…" value={f.q} onChange={(e) => up({ q: e.target.value })} />
        </div>
        <div className="field"><span className="lbl">Type</span>
          <select data-testid="transactions-filter-type" value={f.type} onChange={(e) => up({ type: e.target.value })}>
            <option value="all">All</option><option value="credit">Credits</option><option value="debit">Debits</option>
          </select>
        </div>
        <div className="field"><span className="lbl">Category</span>
          <select data-testid="transactions-filter-category" value={f.cat} onChange={(e) => up({ cat: e.target.value })}>
            <option value="all">All</option>
            {cats.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div className="filters">
        <div className="field"><span className="lbl">Account</span>
          <select data-testid="transactions-filter-account" value={f.acct} onChange={(e) => up({ acct: e.target.value })}>
            <option value="all">All accounts</option>
            {data.accounts.map((a) => <option key={a.code} value={a.code}>{a.name} ••{a.num}</option>)}
          </select>
        </div>
        <div className="field"><span className="lbl">From</span>
          <input data-testid="transactions-filter-from" type="date" value={f.from} onChange={(e) => up({ from: e.target.value })} />
        </div>
        <div className="field"><span className="lbl">To</span>
          <input data-testid="transactions-filter-to" type="date" value={f.to} onChange={(e) => up({ to: e.target.value })} />
        </div>
      </div>
      <div className="card" style={{ padding: "6px 14px 12px" }}>
        <table className="tbl">
          <thead><tr><th>Date</th><th>Description</th><th className="r">Amount</th><th className="r">Balance</th></tr></thead>
          <tbody>
            {rows.length ? (
              rows.map((t) => <TxnRow key={t.id} t={t} showBal acctName={f.acct === "all" ? acct(t.acct)?.name : null} onClick={() => setModal(txnModal(t, acctLabel, closeModal))} />)
            ) : (
              <tr><td colSpan="4" style={{ padding: 26, textAlign: "center", color: "#9d9d9d" }}>No transactions match these filters.</td></tr>
            )}
          </tbody>
        </table>
        <div className="pager">
          <div>{list.length} transaction{list.length === 1 ? "" : "s"} &nbsp;·&nbsp; in {usd0(cin)} / out {usd0(Math.abs(cout))}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <button className="btn btn-ghost btn-sm" data-testid="transactions-prev-page" disabled={page === 1} onClick={() => up({ page: Math.max(1, page - 1) })}>Prev</button>
            <span>{page} / {pages}</span>
            <button className="btn btn-ghost btn-sm" data-testid="transactions-next-page" disabled={page === pages} onClick={() => up({ page: Math.min(pages, page + 1) })}>Next</button>
            <button className="btn btn-lime btn-sm" data-testid="transactions-export-csv-button" onClick={() => {
              if (!list.length) { toast("Nothing to export"); return; }
              download(csvOf(list), "srb-transactions-" + today() + ".csv");
              toast.success(list.length + " transactions exported");
            }}>CSV</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Transfer ---------- */
function Transfer({ data, acct, acctLabel, nav, setModal, closeModal, reload }) {
  const [kind, setKind] = useState("internal");
  const [fromA, setFromA] = useState(data.accounts[0]?.code || "chk");
  const [toA, setToA] = useState(data.accounts[1]?.code || data.accounts[0]?.code || "sav");
  const [eFrom, setEFrom] = useState(data.accounts[0]?.code || "chk");
  const [eTo, setETo] = useState(data.payees[0]?.id || "");
  const [amt, setAmt] = useState("");
  const [date, setDate] = useState(today());
  const [memo, setMemo] = useState("");
  const [hint, setHint] = useState({ text: "No fee. Funds available immediately on internal transfers.", err: false });

  const read = () => {
    const from = kind === "internal" ? fromA : eFrom;
    const to = kind === "internal" ? toA : eTo;
    const raw = (amt || "").replace(/[^0-9.]/g, "");
    return { kind, from, to, amount: Math.round(parseFloat(raw || "0") * 100), date: date || today(), memo: memo.trim() };
  };

  const onAmt = (v) => {
    setAmt(v);
    const x = { from: kind === "internal" ? fromA : eFrom, amount: Math.round(parseFloat((v || "").replace(/[^0-9.]/g, "") || "0") * 100) };
    setHint({ err: false, text: x.amount > 0 ? usd(x.amount) + " from " + acctLabel(x.from) + " · no fee" : "No fee. Funds available immediately on internal transfers." });
  };

  const review = () => {
    const x = read();
    if (!x.amount || x.amount <= 0) return setHint({ err: true, text: "Enter an amount greater than zero." });
    if (x.kind === "internal" && x.from === x.to) return setHint({ err: true, text: "Choose two different accounts." });
    const src = acct(x.from);
    if (x.amount > src.balance) return setHint({ err: true, text: "Insufficient funds in " + src.name + ". Available " + usd(src.balance) + "." });
    const toLabel = x.kind === "internal" ? acctLabel(x.to) : (data.payees.find((p) => p.id === x.to) || {}).name;
    const when = x.date > today() ? "Scheduled for " + dLong(x.date) : "Today, " + dLong(x.date);
    setModal(
      <>
        <h3>Review transfer</h3>
        <div className="srow"><span className="l">From</span><span className="v">{acctLabel(x.from)}</span></div>
        <div className="srow"><span className="l">To</span><span className="v">{toLabel}</span></div>
        <div className="srow"><span className="l">Amount</span><span className="v">{usd(x.amount)}</span></div>
        <div className="srow"><span className="l">Fee</span><span className="v">$0.00</span></div>
        <div className="srow"><span className="l">When</span><span className="v">{when}</span></div>
        {x.memo && <div className="srow"><span className="l">Memo</span><span className="v">{x.memo}</span></div>}
        <div className="srow"><span className="l">Balance after</span><span className="v">{usd(src.balance - x.amount)}</span></div>
        <div style={{ display: "flex", gap: 9, justifyContent: "flex-end", marginTop: 18 }}>
          <button className="btn btn-ghost btn-sm" data-testid="transfer-cancel-button" onClick={closeModal}>Cancel</button>
          <button className="btn btn-navy btn-sm" data-testid="transfer-confirm-modal-button" onClick={() => exec(x)}>Confirm transfer</button>
        </div>
      </>
    );
  };

  const postingRef = useRef(false);
  const exec = async (x) => {
    if (postingRef.current) return;
    postingRef.current = true;
    setModal(
      <>
        <h3>Processing…</h3>
        <div className="sub">Submitting your transfer securely.</div>
      </>
    );
    try {
      const r = await api.post("/transfer", { kind: x.kind, from_acct: x.from, to: x.to, amount: x.amount, date: x.date, memo: x.memo });
      await reload();
      const st = r.data.status;
      const srcName = acctLabel(x.from);
      setModal(
        <>
          <h3>Transfer {st === "scheduled" ? "scheduled" : "complete"}</h3>
          <div className="srow"><span className="l">Amount</span><span className="v">{usd(x.amount)}</span></div>
          <div className="srow"><span className="l">Reference</span><span className="v mono">{r.data.ref}</span></div>
          <div className="srow"><span className="l">Status</span><span className="v">{st === "scheduled" ? "Scheduled · " + dShort(x.date) : "Posted"}</span></div>
          <div className="srow"><span className="l">From</span><span className="v">{srcName}</span></div>
          <div style={{ display: "flex", gap: 9, justifyContent: "flex-end", marginTop: 18 }}>
            <button className="btn btn-ghost btn-sm" data-testid="transfer-again-button" onClick={() => { closeModal(); setAmt(""); setMemo(""); setDate(today()); }}>New transfer</button>
            <button className="btn btn-navy btn-sm" data-testid="transfer-done-button" onClick={() => nav("overview")}>Done</button>
          </div>
        </>
      );
      toast.success(st === "scheduled" ? "Transfer scheduled · " + r.data.ref : "Transfer posted · " + r.data.ref);
    } catch (e) {
      closeModal();
      setHint({ err: true, text: errText(e) });
    } finally {
      postingRef.current = false;
    }
  };

  return (
    <div data-testid="transfer-view">
      <h1 className="h1">Transfer money</h1>
      <div className="sub">Instant internal transfers. External payments post same day or on the date you choose.</div>
      <div className="seg">
        <button className={"seg-b" + (kind === "internal" ? " on" : "")} data-testid="transfer-tab-internal" onClick={() => setKind("internal")}>Between my accounts</button>
        <button className={"seg-b" + (kind === "external" ? " on" : "")} data-testid="transfer-tab-external" onClick={() => setKind("external")}>To a payee</button>
      </div>
      <div className="card pad">
        {kind === "internal" ? (
          <div>
            <div className="field"><span className="lbl">From</span>
              <select data-testid="transfer-from-select" value={fromA} onChange={(e) => setFromA(e.target.value)}>
                {data.accounts.map((a) => <option key={a.code} value={a.code}>{a.name} ••{a.num} — {usd(a.balance)}</option>)}
              </select>
            </div>
            <div className="field"><span className="lbl">To</span>
              <select data-testid="transfer-to-select" value={toA} onChange={(e) => setToA(e.target.value)}>
                {data.accounts.map((a) => <option key={a.code} value={a.code}>{a.name} ••{a.num}</option>)}
              </select>
            </div>
          </div>
        ) : (
          <div>
            <div className="field"><span className="lbl">From</span>
              <select data-testid="transfer-efrom-select" value={eFrom} onChange={(e) => setEFrom(e.target.value)}>
                {data.accounts.map((a) => <option key={a.code} value={a.code}>{a.name} ••{a.num} — {usd(a.balance)}</option>)}
              </select>
            </div>
            <div className="field"><span className="lbl">To payee</span>
              <select data-testid="transfer-payee-select" value={eTo} onChange={(e) => setETo(e.target.value)}>
                {data.payees.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.bank} {p.acct}</option>)}
              </select>
            </div>
          </div>
        )}
        <div className="two">
          <div className="field"><span className="lbl">Amount (USD)</span>
            <input data-testid="transfer-amount-input" inputMode="decimal" placeholder="0.00" value={amt} onChange={(e) => onAmt(e.target.value)} />
          </div>
          <div className="field"><span className="lbl">Send on</span>
            <input data-testid="transfer-date-input" type="date" value={date} min={today()} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>
        <div className="field"><span className="lbl">Memo (optional)</span>
          <input data-testid="transfer-memo-input" maxLength={60} placeholder="What is this for?" value={memo} onChange={(e) => setMemo(e.target.value)} />
        </div>
        <div className={"hint" + (hint.err ? " err" : "")} data-testid="transfer-hint">{hint.text}</div>
        <div className="row-end">
          <button className="btn btn-navy" data-testid="transfer-submit-button" onClick={review}>Review transfer</button>
        </div>
      </div>
      <div className="card" style={{ marginTop: 13 }}>
        <div className="h1" style={{ fontSize: 15, marginBottom: 9 }}>Saved payees</div>
        {data.payees.map((p) => (
          <div className="srow" key={p.id}><span className="l">{p.name}<br /><span className="sub">{p.bank} {p.acct}</span></span><span className="v">{p.type}</span></div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Statements ---------- */
function Statements({ data, openStmt, focus }) {
  const [code, setCode] = useState(data.accounts.some((a) => a.code === focus) ? focus : data.accounts[0]?.code);
  const months = Array.from(
    new Set(data.transactions.filter((t) => t.status === "posted" && t.acct === code).map((t) => t.date.slice(0, 7)))
  ).sort().reverse();
  const accName = data.accounts.find((a) => a.code === code)?.name;
  return (
    <div data-testid="statements-view">
      <h1 className="h1">Statements</h1>
      <div className="sub" style={{ marginBottom: 14 }}>Generated from posted ledger entries. Printable and exportable.</div>
      <div className="filters" style={{ gridTemplateColumns: "1fr" }}>
        <div className="field"><span className="lbl">Account</span>
          <select data-testid="statements-account-select" value={code} onChange={(e) => setCode(e.target.value)}>
            {data.accounts.map((a) => <option key={a.code} value={a.code}>{a.name} ••{a.num}</option>)}
          </select>
        </div>
      </div>
      {months.length === 0 && <div className="sub" data-testid="statements-empty">No statements yet for {accName}.</div>}
      {months.map((m) => {
        const rows = data.transactions.filter((t) => t.status === "posted" && t.date.slice(0, 7) === m && t.acct === code).sort((a, b) => a.seq - b.seq);
        if (!rows.length) return null;
        const close = rows[rows.length - 1].balanceAfter;
        const cin = rows.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
        const cout = rows.filter((t) => t.amount < 0).reduce((s, t) => s + t.amount, 0);
        return (
          <div className="stmt-row" data-testid={`statement-row-${code}-${m}`} key={m} onClick={() => openStmt({ code, m })}>
            <div>
              <div className="dsc">{mName(m)}</div>
              <div className="sub">{rows.length} entries &nbsp;·&nbsp; closing {usd(close)}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="mono" style={{ fontSize: 13, color: "#7fd04a" }}>+{usd0(cin)}</div>
              <div className="mono" style={{ fontSize: 13, color: "#c9c9c9" }}>{usd0(cout)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatementDetail({ data, stmt, nav, acctLabel, setModal, closeModal }) {
  const { code, m } = stmt;
  const account = data.accounts.find((a) => a.code === code);
  const rows = data.transactions.filter((t) => t.status === "posted" && t.date.slice(0, 7) === m && t.acct === code).sort((a, b) => a.seq - b.seq);
  const open = rows.length ? rows[0].balanceAfter - rows[0].amount : 0;
  const close = rows.length ? rows[rows.length - 1].balanceAfter : open;
  const cin = rows.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const cout = rows.filter((t) => t.amount < 0).reduce((s, t) => s + t.amount, 0);
  return (
    <div data-testid="statement-detail-view">
      <div className="print-watermark">SAMPLE DATA · NOT A FINANCIAL DOCUMENT</div>
      <button className="back" onClick={() => nav("statements")}>&#8592; All statements</button>
      <div className="card pad">
        <div className="stat-row">
          <div>
            <div className="h1">{account.name} — statement</div>
            <div className="sub">{mName(m)} &nbsp;·&nbsp; Account ••{account.num} &nbsp;·&nbsp; {data.profile.name}<br />
              {data.profile.owner ? data.profile.owner + " · " : ""}{data.profile.addr}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="sub">Closing balance</div>
            <div className="bal" style={{ fontSize: 24 }}>{usd(close)}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 9, margin: "16px 0 6px" }} className="no-print">
          <button className="btn btn-lime btn-sm" data-testid="statement-print-button" onClick={() => window.print()}>Print</button>
          <button className="btn btn-ghost btn-sm" data-testid="statement-csv-button" onClick={() => { download(csvOf(rows), "srb-statement-" + code + "-" + m + ".csv"); toast.success("Statement exported"); }}>Download CSV</button>
        </div>
        <div className="grid g3" style={{ marginTop: 12 }}>
          <div className="card kpi"><div className="k">Opening balance</div><div className="v" style={{ fontSize: 16 }}>{usd(open)}</div></div>
          <div className="card kpi"><div className="k">Credits</div><div className="v g" style={{ fontSize: 16 }}>+{usd0(cin)}</div></div>
          <div className="card kpi"><div className="k">Debits</div><div className="v" style={{ fontSize: 16 }}>{usd0(cout)}</div></div>
        </div>
        <table className="tbl" style={{ marginTop: 16 }}>
          <thead><tr><th>Date</th><th>Description</th><th className="r">Amount</th><th className="r">Balance</th></tr></thead>
          <tbody>
            {rows.map((t) => <TxnRow key={t.id} t={t} showBal onClick={() => setModal(txnModal(t, acctLabel, closeModal))} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Profile ---------- */
function Profile({ data, reveal, setReveal, reload, onSignOut }) {
  const p = data.profile;
  const [busy, setBusy] = useState(false);
  const doReset = async () => {
    setBusy(true);
    try {
      await api.post("/reset");
      await reload();
      toast.success("Ledger restored");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="profile-view">
      <h1 className="h1">Profile &amp; login</h1>
      <div className="sub" style={{ marginBottom: 16 }}>The credentials tied to this profile.</div>
      <div className="card pad">
        <div className="srow"><span className="l">{p.business ? "Business name" : "Account holder"}</span><span className="v">{p.name}</span></div>
        {p.owner && <div className="srow"><span className="l">Authorized signer</span><span className="v">{p.owner}</span></div>}
        <div className="srow"><span className="l">Username</span><span className="v mono">{p.username}</span></div>
        <div className="srow"><span className="l">Password</span><span className="v mono">
          {p.demo_pass ? (reveal.pw ? p.demo_pass : "••••••••••") : "••••••••••"}
          {p.demo_pass && <button className="reveal" data-testid="profile-reveal-password" onClick={() => setReveal((r) => ({ ...r, pw: !r.pw }))}>{reveal.pw ? " hide" : " show"}</button>}
        </span></div>
        <div className="srow"><span className="l">Email</span><span className="v">{p.email || "—"}</span></div>
        <div className="srow"><span className="l">Phone</span><span className="v">{p.phone || "—"}</span></div>
        <div className="srow"><span className="l">Address</span><span className="v">{p.addr || "—"}</span></div>
        <div className="srow"><span className="l">Customer since</span><span className="v">{p.since ? dLong(p.since) : "—"}</span></div>
        <div className="srow"><span className="l">Last sign-in</span><span className="v">{p.last}</span></div>
        <div className="srow"><span className="l">Role</span><span className="v">{p.role === "admin" ? "Administrator" : "Customer"}</span></div>
      </div>
      <div className="card" style={{ marginTop: 13 }}>
        <div className="h1" style={{ fontSize: 15, marginBottom: 8 }}>Demo controls</div>
        <div className="sub" style={{ marginBottom: 13 }}>Reset restores your seeded ledger and original balances.</div>
        <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
          <button className="btn btn-ghost btn-sm" data-testid="reset-demo-data-button" disabled={busy} onClick={doReset}>Reset demo data</button>
          <button className="btn btn-navy btn-sm" data-testid="profile-signout-button" onClick={onSignOut}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Admin ---------- */
function Admin() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", username: "", password: "", checking: "", savings: "", email: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const r = await api.get("/admin/users");
    setUsers(r.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.username.trim() || !form.password) {
      toast.error("Username and password are required.");
      return;
    }
    const checking = Number(form.checking || "0");
    const savings = Number(form.savings || "0");
    if (!Number.isFinite(checking) || !Number.isFinite(savings) || checking < 0 || savings < 0) {
      toast.error("Opening balances must be non-negative numbers.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/users", {
        name: form.name,
        username: form.username,
        password: form.password,
        email: form.email,
        checking: Math.round(checking * 100),
        savings: Math.round(savings * 100),
      });
      toast.success("User created · " + form.username.toLowerCase());
      setForm({ name: "", username: "", password: "", checking: "", savings: "", email: "" });
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const up = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div data-testid="admin-view">
      <h1 className="h1">Admin · User management</h1>
      <div className="sub" style={{ marginBottom: 16 }}>Create logins for other customers. There is no public self-registration.</div>
      <div className="card pad">
        <div className="h1" style={{ fontSize: 15, marginBottom: 12 }}>Create a new user</div>
        <div className="two">
          <div className="field"><span className="lbl">Full name</span><input data-testid="admin-name-input" value={form.name} onChange={up("name")} placeholder="Jane Doe" /></div>
          <div className="field"><span className="lbl">Username</span><input data-testid="admin-username-input" value={form.username} onChange={up("username")} placeholder="janedoe" /></div>
        </div>
        <div className="two">
          <div className="field"><span className="lbl">Password</span><input data-testid="admin-password-input" value={form.password} onChange={up("password")} placeholder="Temp password" /></div>
          <div className="field"><span className="lbl">Email (optional)</span><input data-testid="admin-email-input" value={form.email} onChange={up("email")} placeholder="jane@example.com" /></div>
        </div>
        <div className="two">
          <div className="field"><span className="lbl">Checking opening (USD)</span><input data-testid="admin-checking-input" inputMode="decimal" value={form.checking} onChange={up("checking")} placeholder="0.00" /></div>
          <div className="field"><span className="lbl">Savings opening (USD)</span><input data-testid="admin-savings-input" inputMode="decimal" value={form.savings} onChange={up("savings")} placeholder="0.00" /></div>
        </div>
        <div className="row-end">
          <button className="btn btn-lime" data-testid="admin-create-user-button" disabled={busy} onClick={create}>{busy ? "Creating…" : "Create user"}</button>
        </div>
      </div>
      <div className="card" style={{ marginTop: 13 }}>
        <div className="h1" style={{ fontSize: 15, marginBottom: 10 }}>All users</div>
        <table className="tbl">
          <thead><tr><th>Name</th><th>Username</th><th className="r">Balance</th></tr></thead>
          <tbody data-testid="admin-users-table">
            {users.map((u) => {
              const total = (u.accounts || []).reduce((s, a) => s + a.balance, 0);
              return (
                <tr key={u.id} data-testid={`admin-user-${u.username}`}>
                  <td><span className="dsc">{u.name}{u.role === "admin" && <span className="badge admin">Admin</span>}<span className="m">{u.email || "—"}</span></span></td>
                  <td className="mono">{u.username}</td>
                  <td className="r mono">{usd(total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
